import React, { useEffect, useMemo, useState } from "react";
import { MdDownload, MdMenuBook } from "react-icons/md";
import Card from "../../../components/card";
import PageHeader, { FilterField } from "../../../components/page/PageHeader";
import api from "../../../services/axios";
import { getDocumentoModulo, getModulosApi } from "../../../services/apiDocsApi";
import { mensagemDoServidor } from "../../../utils/mensagemDoServidor";
import OperationCard from "./components/OperationCard";
import { filterOperations, groupByTag, listOperations, ModuloInfo, OpenApiDoc } from "./openapi";

const baseUrl = () => (api.defaults.baseURL || window.location.origin).replace(/\/+$/, "");

const Esqueleto: React.FC = () => (
  <div className="flex animate-pulse flex-col gap-2" aria-busy="true" aria-label="Carregando">
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="h-10 rounded-xl bg-gray-100 dark:bg-navy-800" />
    ))}
  </div>
);

const Aviso: React.FC<{ children: React.ReactNode; erro?: boolean }> = ({ children, erro }) => (
  <p
    role={erro ? "alert" : undefined}
    className={`py-10 text-center text-sm ${erro ? "text-red-700 dark:text-red-300" : "text-gray-700 dark:text-gray-300"}`}
  >
    {children}
  </p>
);

const ApiDocsPage: React.FC = () => {
  const [modulos, setModulos] = useState<ModuloInfo[]>([]);
  const [carregandoModulos, setCarregandoModulos] = useState(true);
  const [erroModulos, setErroModulos] = useState<string | null>(null);

  const [moduloId, setModuloId] = useState<string | null>(null);
  const [doc, setDoc] = useState<OpenApiDoc | null>(null);
  const [carregandoDoc, setCarregandoDoc] = useState(false);
  const [erroDoc, setErroDoc] = useState<string | null>(null);

  const [busca, setBusca] = useState("");

  useEffect(() => {
    let vivo = true;
    getModulosApi()
      .then((lista) => {
        if (!vivo) return;
        setModulos(lista);
        setModuloId((atual) => atual ?? lista[0]?.id ?? null);
      })
      .catch((e) => vivo && setErroModulos(mensagemDoServidor(e, "Não foi possível carregar os módulos. Tente recarregar a página.")))
      .finally(() => vivo && setCarregandoModulos(false));
    return () => {
      vivo = false;
    };
  }, []);

  useEffect(() => {
    if (!moduloId) return;
    let vivo = true; // troca rápida de módulo: a resposta antiga não pode sobrescrever a nova
    setCarregandoDoc(true);
    setErroDoc(null);
    setDoc(null);
    getDocumentoModulo(moduloId)
      .then((d) => vivo && setDoc(d))
      .catch((e) => vivo && setErroDoc(mensagemDoServidor(e, "Não foi possível carregar a documentação deste módulo. Tente de novo.")))
      .finally(() => vivo && setCarregandoDoc(false));
    return () => {
      vivo = false;
    };
  }, [moduloId]);

  const operacoes = useMemo(() => (doc ? listOperations(doc) : []), [doc]);
  const grupos = useMemo(() => groupByTag(filterOperations(operacoes, busca)), [operacoes, busca]);
  const modulo = modulos.find((m) => m.id === moduloId);

  const baixar = () => {
    if (!doc || !moduloId) return;
    const url = URL.createObjectURL(new Blob([JSON.stringify(doc, null, 2)], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `nina-api-${moduloId}.openapi.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-5 flex animate-fade-up flex-col gap-4">
      <PageHeader
        icon={<MdMenuBook className="h-5 w-5" />}
        title="API do NINA"
        filters={
          <FilterField label="Buscar na rota ou no resumo" htmlFor="api-docs-busca">
            <input
              id="api-docs-busca"
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Ex.: chamados, /Ticket, POST"
              disabled={!doc}
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 disabled:opacity-50 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
          </FilterField>
        }
        actions={
          <button
            type="button"
            onClick={baixar}
            disabled={!doc}
            className="flex items-center gap-1.5 rounded-xl bg-ink-900 px-4 py-2 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
          >
            <MdDownload className="h-4 w-4" aria-hidden="true" /> Baixar OpenAPI (JSON)
          </button>
        }
      />

      <Card extra="p-4">
        <p className="text-sm font-bold text-navy-700 dark:text-white">Como autenticar</p>
        <ul className="mt-1.5 list-disc pl-5 text-xs text-gray-700 dark:text-gray-300">
          <li>
            URL base: <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono dark:bg-navy-900">{baseUrl()}</code>
          </li>
          <li>
            Usuário logado: header <code className="font-mono">Authorization: Bearer &lt;token do login&gt;</code>.
          </li>
          <li>
            Integração: header <code className="font-mono">X-Api-Key: &lt;chave&gt;</code>, com a chave criada em “API Keys”. A chave pode ser
            restrita a alguns módulos; fora deles a API responde 403.
          </li>
          <li>Cada operação mostra a permissão exigida; “Anônimo” não exige autenticação.</li>
        </ul>
      </Card>

      {carregandoModulos ? (
        <Card extra="p-5">
          <Esqueleto />
        </Card>
      ) : erroModulos ? (
        <Card extra="p-5">
          <Aviso erro>{erroModulos}</Aviso>
        </Card>
      ) : modulos.length === 0 ? (
        <Card extra="p-5">
          <Aviso>Nenhum módulo de API disponível para este acesso. Aqui aparecem os módulos que o seu usuário ou a sua chave podem consultar.</Aviso>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
          <Card extra="h-fit p-3">
            <nav aria-label="Módulos da API" className="flex flex-col gap-1">
              {modulos.map((m) => {
                const ativo = m.id === moduloId;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setModuloId(m.id)}
                    aria-current={ativo ? "true" : undefined}
                    className={`flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-sm font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40 ${
                      ativo
                        ? "bg-dv-blue-600 text-white shadow-dv-md"
                        : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-navy-700"
                    }`}
                  >
                    <span>{m.nome}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] tabular-nums ${ativo ? "bg-white/20" : "bg-gray-100 dark:bg-navy-900"}`}>
                      {m.endpoints}
                    </span>
                  </button>
                );
              })}
            </nav>
          </Card>

          <Card extra="p-4">
            {modulo && (
              <div className="mb-3">
                <h2 className="text-lg font-extrabold text-navy-700 dark:text-white">{modulo.nome}</h2>
                <p className="text-xs text-gray-700 dark:text-gray-300">{modulo.descricao}</p>
              </div>
            )}
            {carregandoDoc ? (
              <Esqueleto />
            ) : erroDoc ? (
              <Aviso erro>{erroDoc}</Aviso>
            ) : doc && operacoes.length === 0 ? (
              <Aviso>Este módulo ainda não tem operações documentadas.</Aviso>
            ) : doc && grupos.length === 0 ? (
              <Aviso>Nenhuma operação encontrada para “{busca}”. Tente outro termo.</Aviso>
            ) : (
              <div className="flex flex-col gap-5">
                {grupos.map((g) => (
                  <section key={g.tag} aria-label={g.tag}>
                    <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                      {g.tag} <span className="font-normal tabular-nums">({g.operations.length})</span>
                    </h3>
                    <div className="flex flex-col gap-2">
                      {g.operations.map((op) => (
                        <OperationCard key={op.key} doc={doc!} op={op} />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

export default ApiDocsPage;
